import { Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { E_Patient_Actions_Type } from "@backend/graph/patient_actions/patient-action-base";
import { T_ValidAppointment } from "@backend/graph/patient_actions/routine-appointment-base";
import Bugsnag from "@bugsnag/js";
import dayjs from "dayjs";
import { E_AppointmentEventActions } from "@apps/patient-core/src/app/data_model/appointment";
import { BookableAppointmentCategoryEntry } from "@apps/patient-core/src/app/data_model/bookable-appointment";
import { PatientActionsEntry } from "@apps/patient-core/src/app/data_model/patient-actions";
import { CacheService } from "@apps/patient-core/src/app/services/cache.service";
import { HttpService } from "@apps/patient-core/src/app/services/http.service";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { MedicalHistoryService } from "@apps/patient-core/src/app/services/medical-history.service";
import { NavigationService } from "@apps/patient-core/src/app/services/navigation.service";
import { NhsPrService } from "@apps/patient-core/src/app/services/nhs-pr.service";
import { NotificationService } from "@apps/patient-core/src/app/services/notification.service";
import { PatientActionsCacheService } from "@apps/patient-core/src/app/services/patient-actions-cache.service";
import { BasePatientActionsService } from "@apps/patient-core/src/app/services/abstracts/patient-actions.service";
import { BasePatientsService } from "@apps/patient-core/src/app/services/abstracts/patients.service";
import { BaseSessionService } from "@apps/patient-core/src/app/services/abstracts/session.service";
import { TreatmentPlansService } from "@apps/patient-core/src/app/services/treatment-plans.service";
import { filter } from "rxjs";
import { AppointmentsService } from "../appointments.service";
import { BookableApptsService } from "../bookable-appts.service";
import { AppointmentAnalyticsService } from "../analytics/appointment-analytics.service";
import { ShortCodesService } from "../short-codes.service";

export function PATIENT_ACTIONS_QUERY(short_code_id?: string | null) {
  const args = `${short_code_id ? `short_code_id: "${short_code_id}"` : ""}`;
  return `{
  patient_actions${args.length > 0 ? `(${args})` : ""} {
    items {
      appointment_ids
      text
      type
      pms_action_type
      required
      data {
        meta_data {
          medical_history_id
          exemption_id
          patient_exemption_code
          form_type
          treatment_plans {
            total_cost
          }
          smile_goal {
            goal
            timeframe
            motivation
          }
        }
        patient_details_missing_data {
          sections
          fields
        }
        patient_details_completed_percentage
        routine_appt_data {
          site_id
          id
          is_nhs
          category_id
          appointment_name
          appointment_type_id
        }
        estimates {
          id
          template_name
        }
      }
    }
  }
}`;
}

@Injectable({
  providedIn: "root",
})
export class PatientActionsService extends BasePatientActionsService {
  private _shortCodeId: string | null = null;
  constructor(
    protected _navigationService: NavigationService,
    protected _authService: BaseAuthService,
    protected _httpService: HttpService,
    protected _medicalHistoryService: MedicalHistoryService,
    protected _nhsPrService: NhsPrService,
    protected _notificationService: NotificationService,
    protected _patientsService: BasePatientsService,
    protected _treatmentPlansService: TreatmentPlansService,
    protected _cacheService: CacheService,
    protected _route: ActivatedRoute,
    protected _patientActionsCacheService: PatientActionsCacheService,
    protected _sessionService: BaseSessionService,
    private _appointmentsService: AppointmentsService,
    private _bookableApptsService: BookableApptsService,
    private _analyticsService: AppointmentAnalyticsService,
    private _shortCodesService: ShortCodesService
  ) {
    super(
      _navigationService,
      _authService,
      _httpService,
      _medicalHistoryService,
      _nhsPrService,
      _notificationService,
      _patientsService,
      _treatmentPlansService,
      _cacheService,
      _route,
      _patientActionsCacheService,
      _sessionService
    );

    this._appointmentsService.onAppointmentStatusEvents
      .pipe(filter((statusEvent) => statusEvent?.action === E_AppointmentEventActions.APPOINTMENTS_UPDATED))
      .subscribe((event) => {
        // instantly remove any patient actions which no longer have an associated appointment (because its been cancelled)
        this.patientActions = this.patientActions.filter((pa) => {
          if (!pa.appointment_ids?.length) return true;
          return event?.appointments?.find((a) => a.id && pa.appointment_ids.includes(a.id));
        });

        this.fetchPatientActions(true);
      });

    this._shortCodeId = this._shortCodesService.short_code_id;
  }

  protected getPatientActionsQuery(): string {
    return PATIENT_ACTIONS_QUERY(this._shortCodeId);
  }

  public navigateToAction(action: PatientActionsEntry): void {
    switch (action.type) {
      case E_Patient_Actions_Type.NHS_PR:
        this._handleNhsPrAction(action);
        break;
      case E_Patient_Actions_Type.MEDICAL_HISTORY:
        this._goToFormPage("my-dental/medical-history/update-medical-history");
        break;
      case E_Patient_Actions_Type.ROUTINE_DENTAL_EXAM:
        this.handleRoutineAppt(action.data.routine_appt_data, this._patientsService.patientInfo?.dentist_recall_date);
        break;
      case E_Patient_Actions_Type.ROUTINE_HYGIENE_APPT:
        this.handleRoutineAppt(action.data.routine_appt_data, this._patientsService.patientInfo?.hygienist_recall_date);
        break;
      case E_Patient_Actions_Type.TREATMENT_PLAN_ESTIMATE:
        this._navigateToTreatmentPlanEstimate(action);
        break;
      case E_Patient_Actions_Type.PATIENT_DETAILS:
        this._handlePatientDetailsAction(action);
        break;
      case E_Patient_Actions_Type.SMILE_GOAL:
        this._goToFormPage("my-dental/smile-goals/goal");
        break;
      default:
        break;
    }
  }

  public handleRoutineAppt(appts: Array<T_ValidAppointment> | undefined, recallDate: string | null | undefined) {
    // Resets data when navigating away from routine appointment without booking
    this._bookableApptsService.clearPreviousSelections();
    // Fallback if there are any issues with the data
    if (!appts || appts.length === 0 || !recallDate) {
      this._bookableApptsService.navigate("");
      Bugsnag.notify(new Error("Unable to handle routine appointment"));
      return;
    }

    try {
      this._analyticsService.trackBookingStarted();
      this._setRoutineApptSelectedCategory(appts[0]);

      if (appts.length === 1) {
        this._handleRoutineApptSingle(appts[0], recallDate);
      } else {
        this._handleRoutineApptMulti(appts);
      }
    } catch (e) {
      this._bookableApptsService.navigate("");
    }
  }

  private _handleRoutineApptMulti(appts: Array<T_ValidAppointment>) {
    /*
      If appointment_list_first is true we preselect a site appointment matching the correct ID which the user will change at the location page
      If the appointments are all of the same appointment type and the user cannot book together, then we navigate to the location page
      If the appointments are all of the same appointment type and the user can book together, then we navigate to the additional appointments page
    */
    if (this._bookableApptsService.appointment_list_first) {
      const hasCommonAppointmentType = appts.every((appt) => appt.appointment_type_id === appts[0].appointment_type_id);

      this._setRoutineApptSelectedAppointment(appts[0]);
      if (hasCommonAppointmentType && this._bookableApptsService.selectedAppointment) {
        const route = this._bookableApptsService.shouldOfferBookTogether ? "additional-appointments" : "location";
        this._bookableApptsService.navigate(route);
        return;
      }
    }
    // If there are multiple appointments, then we allow the patient to choose their site appointment type. The backend prioritises the NHS appointments first
    this._bookableApptsService.navigate("appointment");
  }

  private _setRoutineApptSelectedCategory(appt: T_ValidAppointment) {
    let found_category: BookableAppointmentCategoryEntry | undefined;
    const { appointment_list_first } = this._bookableApptsService;

    // The category ID's and site ID's are the same for all the appointments as the patient can either be lapsed/existing and they can only book a routine appointment at their own site
    if (appointment_list_first) {
      found_category = (this._bookableApptsService.bookableAppts as Array<BookableAppointmentCategoryEntry>).find(
        (category) => category.id === appt.category_id
      );
    } else {
      this._bookableApptsService.selectSite(appt.site_id);
      found_category = this._bookableApptsService.selectedSite?.appointment_categories.find((category) => category.id === appt.category_id);
    }

    if (found_category) {
      this._bookableApptsService.selectedCategory = found_category;
    } else {
      const error = "Unable to set the selected category for routine appointment";
      Bugsnag.notify(new Error(error));
      throw error;
    }
  }

  private _setRoutineApptSelectedAppointment(appt: T_ValidAppointment) {
    const found_appointment = this._bookableApptsService.selectedCategory?.site_appointment_types.find((at) => at.id === appt.id);

    if (found_appointment) {
      this._bookableApptsService.selectedAppointment = found_appointment;
    } else {
      const error = "Unable to set the selected appointment for routine appointment";
      Bugsnag.notify(new Error(error));
      throw error;
    }
  }

  private _handleRoutineApptSingle(appt: T_ValidAppointment, recallDate: string) {
    const isPatientNhs = this._patientsService?.patientInfo?.is_nhs;
    const { appointment_list_first } = this._bookableApptsService;

    let searchFromDate = dayjs(recallDate).add(-2, "week");
    const today = dayjs();
    if (searchFromDate.isBefore(today)) searchFromDate = today;
    const isApptNhs = appt.is_nhs;

    this._setRoutineApptSelectedAppointment(appt);

    // Check the patients plan matches that of the appointment type
    if (isPatientNhs === isApptNhs) {
      // We can preselect the first appointment and allow the patient to select the additional appointment(s)
      if (this._bookableApptsService.shouldOfferBookTogether) {
        this._bookableApptsService.navigate("additional-appointments");
        return;
      }

      // The user must always select the site if the appointment_list_first is true
      if (appointment_list_first) {
        this._bookableApptsService.navigate("location");
        return;
      }

      // If the patient and the appointment are both NHS or both private, then we can navigate to the availability page
      this._bookableApptsService.navigateToAvailability(searchFromDate);
    } else {
      /*
          If the patients NHS status does not match the appointment and the category supports NHS appointments, then we navigate to the appointment page
          This is to ensure that the patient is aware they may need to pay more for the appointment
        */
      if (this._bookableApptsService.selectedCategory?.supports_nhs) {
        this._bookableApptsService.navigate("appointment");
        return;
      } else if (this._bookableApptsService.shouldOfferBookTogether) {
        this._bookableApptsService.navigate("additional-appointments");
        return;
      } else {
        this._bookableApptsService.navigateToAvailability(searchFromDate);
        return;
      }
    }
  }
}
